import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import { InnerLayout, InnerLayoutSection } from '../components/layout/InnerLayout';
import { DatastoreCard } from '../components/datastore/DatastoreCard';
import {
  Table2,
  Zap,
  ArrowRight,
  HardDrive,
  Plus,
  Inbox,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { formatFileSize } from '../utils/fileUrl';

export const DashboardView: React.FC = () => {
  const { orgId: paramOrgId, projectId: paramProjectId } = useParams<{ orgId?: string; projectId?: string }>();
  const { currentProject, currentOrg, tables, canAdmin, canEdit } = useAuth();
  const [totalIngested, setTotalIngested] = useState<number>(() => {
    return (currentProject?.submission_count && currentProject.submission_count > 0)
      ? currentProject.submission_count
      : tables.reduce((acc, t) => acc + (t.submission_count || 0), 0);
  });
  const [submissions24h, setSubmissions24h] = useState<number>(0);
  const [validIngested, setValidIngested] = useState<number>(0);
  const [spamIngested, setSpamIngested] = useState<number>(0);
  const [latencyP50, setLatencyP50] = useState<number | null>(null);
  const [projectFilesCount, setProjectFilesCount] = useState<number>(() => {
    const totalProjectFiles = tables.reduce((acc, t) => acc + (t.files_count || 0), 0);
    return (currentProject?.files_count && currentProject.files_count > 0)
      ? currentProject.files_count
      : totalProjectFiles;
  });
  const [projectStorageBytes, setProjectStorageBytes] = useState<number>(() => {
    const totalProjectStorage = tables.reduce((acc, t) => acc + (t.storage_bytes || 0), 0);
    return (currentProject?.storage_bytes && currentProject.storage_bytes > 0)
      ? currentProject.storage_bytes
      : totalProjectStorage;
  });

  const currentProjectId = paramProjectId || currentProject?.id;
  const currentOrgId = paramOrgId || currentOrg?.id;

  // Reset metrics when switching projects
  useEffect(() => {
    setTotalIngested(0);
    setSubmissions24h(0);
    setValidIngested(0);
    setSpamIngested(0);
    setLatencyP50(null);
    setProjectFilesCount(0);
    setProjectStorageBytes(0);
  }, [currentProjectId]);

  const fetchDashboardMetrics = React.useCallback(async () => {
    if (!currentProjectId) return;

    try {
      const telemetryRes = await apiClient<any>(
        `/projects/${currentProjectId}/analytics?range=all`
      );
      if (telemetryRes) {
        setTotalIngested(telemetryRes.total_submissions ?? 0);
        setSubmissions24h(telemetryRes.submissions_24h ?? 0);
        setValidIngested(telemetryRes.valid_submissions ?? 0);
        setSpamIngested(telemetryRes.spam_submissions ?? 0);
        setLatencyP50(telemetryRes.latency_p50_ms ?? null);
      }
    } catch {
      // Degrade gracefully using table counts
    }

    try {
      const filesRes = await apiClient<any>(
        `/projects/${currentProjectId}/files?limit=1`
      );
      if (filesRes) {
        setProjectFilesCount(filesRes.total_files || filesRes.total || 0);
        setProjectStorageBytes(filesRes.total_size_bytes || filesRes.total_bytes || 0);
      }
    } catch {
      // Storage files counter fallback
    }
  }, [currentProjectId]);

  useEffect(() => {
    fetchDashboardMetrics();
  }, [fetchDashboardMetrics]);

  const lastFocusRef = React.useRef<number>(0);
  useEffect(() => {
    const handleFocus = () => {
      const now = Date.now();
      if (now - lastFocusRef.current < 4000) return;
      lastFocusRef.current = now;
      fetchDashboardMetrics();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchDashboardMetrics]);

  useEffect(() => {
    if (!currentProject) return;
    const interval = setInterval(() => {
      fetchDashboardMetrics();
    }, 10000);
    return () => clearInterval(interval);
  }, [currentProject, fetchDashboardMetrics]);

  return (
    <InnerLayout
      header={{
        title: currentProject?.name || 'Project Overview',
        description:
          'Hedgehog collecting structured form submissions on its spikes with asynchronous notifications, automatic honeypot quarantine, and interactive spreadsheet view.',
      }}
    >
      <div className="space-y-6">
        {/* Pipeline Telemetry Section */}
        <InnerLayoutSection
          title="Pipeline Performance"
          actions={[
            {
              label: 'Analytics & trends',
              icon: ArrowRight,
              variant: 'ghost',
              to: currentOrgId && currentProjectId
                ? `/organization/${currentOrgId}/projects/${currentProjectId}/analytics`
                : `/analytics`,
            },
          ]}
        >
          {(() => {
            const validPct = totalIngested > 0 ? Math.round((validIngested / totalIngested) * 100) : 100;
            const latencyPct =
              typeof latencyP50 === 'number' && latencyP50 > 0
                ? Math.min(100, Math.round((latencyP50 / 50) * 100))
                : 0;
            const storageLimit = currentOrg?.max_storage_bytes || 104857600;
            const storagePct = storageLimit > 0 ? Math.min(100, Math.round((projectStorageBytes / storageLimit) * 100)) : 0;

            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Meter 1: Total Ingested */}
                <div className="p-3 bg-cf-surface border border-cf-border rounded-lg space-y-2">
                  <div className="flex items-center justify-between text-xs gap-2">
                    <span className="text-cf-textSecondary font-medium flex items-center gap-1.5 min-w-0 truncate">
                      <Inbox size={13} className="text-cf-textMuted shrink-0" />
                      <span className="truncate">Total Ingested</span>
                    </span>
                    <span className="font-semibold text-cf-textPrimary font-mono tabular-nums shrink-0 whitespace-nowrap">
                      {totalIngested.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-cf-bg border border-cf-border/50 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-cf-orange rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(100, Math.max(5, (totalIngested / (currentOrg?.max_monthly_submissions || 1000)) * 100))}%`,
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] text-cf-textMuted">
                    <span>{submissions24h.toLocaleString()} in last 24h</span>
                    <span>
                      {Math.round(
                        (totalIngested / (currentOrg?.max_monthly_submissions || 1000)) * 100
                      )}
                      % quota
                    </span>
                  </div>
                </div>

                {/* Meter 2: Quarantine & Spam */}
                <div className="p-3 bg-cf-surface border border-cf-border rounded-lg space-y-2">
                  <div className="flex items-center justify-between text-xs gap-2">
                    <span className="text-cf-textSecondary font-medium flex items-center gap-1.5 min-w-0 truncate">
                      <ShieldCheck size={13} className="text-cf-textMuted shrink-0" />
                      <span className="truncate">Honeypot Filter</span>
                    </span>
                    <span className="font-semibold text-cf-textPrimary font-mono tabular-nums shrink-0 whitespace-nowrap">
                      {validPct}% Clean
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-cf-bg border border-cf-border/50 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        spamIngested > 0 ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                      style={{ width: `${validPct}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] text-cf-textMuted">
                    <span>
                      {spamIngested === 0
                        ? 'Zero honeypot spam'
                        : `${spamIngested.toLocaleString()} spam quarantined`}
                    </span>
                    <span>
                      {totalIngested > 0 ? `${validIngested.toLocaleString()} clean` : 'Protected'}
                    </span>
                  </div>
                </div>

                {/* Meter 3: Ingestion Latency */}
                <div className="p-3 bg-cf-surface border border-cf-border rounded-lg space-y-2">
                  <div className="flex items-center justify-between text-xs gap-2">
                    <span className="text-cf-textSecondary font-medium flex items-center gap-1.5 min-w-0 truncate">
                      <Zap size={13} className="text-cf-textMuted shrink-0" />
                      <span className="truncate">Ingestion Latency</span>
                    </span>
                    <span className="font-semibold text-cf-textPrimary font-mono tabular-nums shrink-0 whitespace-nowrap">
                      {typeof latencyP50 === 'number' && latencyP50 > 0
                        ? `${latencyP50.toFixed(1)} ms`
                        : '< 50 ms'}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-cf-bg border border-cf-border/50 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        latencyPct <= 80
                          ? 'bg-emerald-500'
                          : latencyPct <= 100
                          ? 'bg-amber-500'
                          : 'bg-rose-500'
                      }`}
                      style={{ width: `${latencyPct || 15}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] text-cf-textMuted">
                    <span>p50 write acknowledgment</span>
                    <span>Sub-50ms target</span>
                  </div>
                </div>

                {/* Meter 4: Project Storage */}
                <div className="p-3 bg-cf-surface border border-cf-border rounded-lg space-y-2">
                  <div className="flex items-center justify-between text-xs gap-2">
                    <span className="text-cf-textSecondary font-medium flex items-center gap-1.5 min-w-0 truncate">
                      <HardDrive size={13} className="text-cf-textMuted shrink-0" />
                      <span className="truncate">Project Storage</span>
                    </span>
                    <span className="font-semibold text-cf-textPrimary font-mono tabular-nums shrink-0 whitespace-nowrap">
                      {formatFileSize(projectStorageBytes)}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-cf-bg border border-cf-border/50 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        storagePct >= 90
                          ? 'bg-rose-500'
                          : storagePct >= 75
                          ? 'bg-amber-500'
                          : 'bg-cf-orange'
                      }`}
                      style={{ width: `${Math.max(2, storagePct)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[11px] text-cf-textMuted">
                    <span>
                      {projectFilesCount.toLocaleString()} {projectFilesCount === 1 ? 'file' : 'files'}
                    </span>
                    <span>{storagePct}% of org limit</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </InnerLayoutSection>

        {/* Datastores Section */}
        <InnerLayoutSection
          title={`Datastores in this project (${tables.length})`}
          actions={
            canAdmin || canEdit
              ? [
                  {
                    label: 'New Datastore',
                    icon: Plus,
                    variant: 'primary',
                    onClick: () => window.dispatchEvent(new CustomEvent('ericius:open-create-table')),
                  },
                ]
              : undefined
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {tables.slice(0, 7).map((t) => (
              <DatastoreCard
                key={t.id}
                table={t}
                orgId={currentOrgId}
                projectId={currentProjectId}
              />
            ))}

            {tables.length > 7 && (
              <Link
                to={
                  currentOrgId && currentProjectId
                    ? `/organization/${currentOrgId}/projects/${currentProjectId}/datastores`
                    : `/datastores`
                }
                className="p-5 rounded-xl border border-dashed border-cf-border hover:border-cf-borderActive hover:bg-cf-surface/50 flex flex-col items-center justify-center text-center transition-all group min-h-[160px]"
              >
                <div className="w-10 h-10 rounded-full bg-cf-bg border border-cf-border flex items-center justify-center text-cf-textMuted group-hover:text-cf-orange group-hover:border-cf-orange/50 transition-colors mb-2">
                  <Table2 size={20} />
                </div>
                <span className="text-xs font-semibold text-cf-textPrimary">
                  View All Datastores ({tables.length})
                </span>
                <span className="text-xs text-cf-textMuted mt-0.5">
                  +{tables.length - 7} more datastores
                </span>
              </Link>
            )}

            {tables.length === 0 && (
              <div className="col-span-full py-12 px-4 text-center rounded-xl bg-cf-surface border border-cf-border">
                <Table2 size={32} className="mx-auto text-cf-textMuted mb-2" />
                <div className="text-xs font-semibold text-cf-textPrimary uppercase tracking-wider">
                  No datastores yet
                </div>
                <p className="text-xs text-cf-textSecondary mt-1 max-w-sm mx-auto">
                  Create a datastore to start collecting structured form submissions with automated notification rules.
                </p>
                {(canAdmin || canEdit) && (
                  <div className="mt-4">
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<Plus size={14} />}
                      onClick={() => window.dispatchEvent(new CustomEvent('ericius:open-create-table'))}
                    >
                      Create Datastore
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </InnerLayoutSection>
      </div>
    </InnerLayout>
  );
};
